/**
 * AAB listing – sticky navigation and search for every listing layout.
 *
 * One script for the "AAB – Main Index" block, the [aab_main] shortcode and
 * the classic archive template (all rendered by Blocks::render_listing()).
 *
 *  - Search: instant filter over each item's search index (title, short
 *    answer, SEO keywords, long answer — shipped as text inside an inert
 *    <template>, never in an attribute); every typed word must match. Curated
 *    "Show more" groups that contain a match open automatically, groups with
 *    no match hide, and a "no results" message appears when nothing matches.
 *  - Pills: jump links that scroll to the category section, with the active
 *    pill tracking the scroll position. Pills wrap — never a horizontal
 *    scroll — and beyond the visible limit a "+N more" pill reveals the rest.
 *  - Answer Library layout: a "By category / A–Z" toggle regroups the existing
 *    nodes (open accordions stay open), rebuilds the pills as letters, and a
 *    live count follows the current search. ?aab_view=az is honoured on load
 *    and mirrored to the URL.
 *
 * @since 0.4.2 (supersedes archive-nav.js from 0.3.29)
 */
(function () {
	'use strict';

	var HIDDEN = 'wg-aab-hidden';

	function forEach( list, fn ) {
		Array.prototype.forEach.call( list, fn );
	}

	/**
	 * Minimal sprintf supporting %s and positional %1$s placeholders.
	 */
	function sprintf( template ) {
		var args = Array.prototype.slice.call( arguments, 1 );
		var auto = 0;
		return String( template ).replace( /%(\d+\$)?s/g, function ( match, pos ) {
			var index = pos ? parseInt( pos, 10 ) - 1 : auto++;
			return args[ index ] !== undefined ? args[ index ] : '';
		} );
	}

	function closest( el, selector ) {
		return el && el.closest ? el.closest( selector ) : null;
	}

	/**
	 * Text of one part ('search' or 'sort') of an item's inert
	 * <template class="wg-aab-item__index">. PHP ships content-derived text
	 * there rather than in attributes: markup a filter injects into the
	 * rendered block (e.g. a theme turning a {{phone}} placeholder into a tel:
	 * link) becomes inert nodes, so textContent is still clean text.
	 */
	function indexText( item, part ) {
		var tpl   = item.querySelector( 'template.wg-aab-item__index' );
		var scope = tpl ? ( tpl.content || tpl ) : null;
		var node  = scope ? scope.querySelector( '.wg-aab-item__' + part ) : null;
		return node ? node.textContent : '';
	}

	/**
	 * Lower-cases and folds typographic punctuation (curly quotes, dashes,
	 * ellipsis, no-break spaces) so a query typed with straight quotes matches
	 * an index that wptexturize or an author's smart quotes have curled.
	 */
	function normalizeText( text ) {
		return String( text || '' )
			.toLowerCase()
			.replace( /[\u2018\u2019\u201A\u201B\u2032]/g, "'" )
			.replace( /[\u201C\u201D\u201E\u201F\u2033]/g, '"' )
			.replace( /[\u2010-\u2015\u2212]/g, '-' )
			.replace( /\u2026/g, '...' )
			.replace( /[\u00A0\s]+/g, ' ' )
			.trim();
	}

	function initListing( root ) {
		var groupsWrap = root.querySelector( '.wg-aab-listing__groups' );
		if ( ! groupsWrap ) {
			return;
		}

		var nav         = root.querySelector( '.wg-aab-archive-nav' );
		var searchInput = nav ? nav.querySelector( '.wg-aab-archive-search' ) : null;
		var pillsWrap   = nav ? nav.querySelector( '.wg-aab-archive-nav__pills' ) : null;
		var countEl     = nav ? nav.querySelector( '.wg-aab-archive-nav__count' ) : null;
		var viewBtns    = nav ? nav.querySelectorAll( '.wg-aab-nav-view' ) : [];
		var noResults   = root.querySelector( '.wg-aab-no-results' );
		var adminBar    = document.getElementById( 'wpadminbar' );
		var layout      = root.getAttribute( 'data-layout' ) || 'full';
		var baseId      = root.id || 'wg-aab-listing';

		var data   = {};
		var dataEl = root.querySelector( '.wg-aab-listing__data' );
		if ( dataEl ) {
			try {
				data = JSON.parse( dataEl.textContent || '{}' ) || {};
			} catch ( e ) {
				data = {};
			}
		}
		var groupMeta = data.groups || {};
		var i18n      = data.i18n || {};

		// Pre-compute item metadata once. Search index and sort key come from
		// the item's <template> (see indexText). Keep legacy attributes as a
		// fallback for cached 0.4.2 markup, then use visible text.
		var items = [];
		forEach( groupsWrap.querySelectorAll( '.wg-aab-item' ), function ( el ) {
			var title = el.querySelector( 'summary h3, .aab-title' );
			items.push( {
				el:     el,
				search: normalizeText( indexText( el, 'search' ) || el.getAttribute( 'data-search' ) || el.textContent ),
				group:  el.getAttribute( 'data-group' ) || '',
				letter: el.getAttribute( 'data-letter' ) || '#',
				sort:   normalizeText( indexText( el, 'sort' ) || el.getAttribute( 'data-sort' ) || ( title ? title.textContent : '' ) ),
				order:  parseInt( el.getAttribute( 'data-order' ) || '0', 10 )
			} );
		} );
		var total = items.length;

		// -------------------------------------------------------------------
		// Sticky nav: theme header + persistent admin-bar offset + shadow once stuck
		// -------------------------------------------------------------------
		function syncAdminBarOffset() {
			if ( ! nav ) {
				return;
			}
			var offset = 0;
			if ( adminBar ) {
				var style = window.getComputedStyle( adminBar );
				// WordPress makes the toolbar absolute on small screens, where it
				// scrolls away and must not leave a permanent gap above the nav.
				if ( style.position === 'fixed' && style.visibility !== 'hidden' ) {
					offset = Math.max( 0, adminBar.getBoundingClientRect().bottom );
				}
			}
			var value = offset + 'px';
			if ( nav.style.getPropertyValue( '--wg-aab-admin-bar-offset' ) !== value ) {
				nav.style.setProperty( '--wg-aab-admin-bar-offset', value );
			}
		}

		if ( nav ) {
			syncAdminBarOffset();
			var sentinel = document.createElement( 'div' );
			sentinel.className = 'wg-aab-nav-sentinel';
			sentinel.setAttribute( 'aria-hidden', 'true' );
			nav.parentNode.insertBefore( sentinel, nav );

			if ( 'IntersectionObserver' in window ) {
				new IntersectionObserver( function ( entries ) {
					nav.classList.toggle( 'wg-aab-archive-nav--scrolled', ! entries[0].isIntersecting );
				}, { threshold: 0 } ).observe( sentinel );
			}
		}

		function navOffset() {
			syncAdminBarOffset();
			// Read the resolved CSS top so responsive theme offsets and the
			// toolbar are counted exactly once for both jumps and active pills.
			var top = nav ? parseFloat( window.getComputedStyle( nav ).top ) || 0 : 0;
			return ( nav ? nav.offsetHeight : 0 ) + Math.max( 0, top ) + 16;
		}

		function scrollToElement( el ) {
			var top = el.getBoundingClientRect().top + window.pageYOffset - navOffset();
			window.scrollTo( { top: Math.max( 0, top ), behavior: 'smooth' } );
		}

		// -------------------------------------------------------------------
		// Pills: jump links + "+N more" overflow toggle
		// -------------------------------------------------------------------
		function pills() {
			return pillsWrap ? pillsWrap.querySelectorAll( '.wg-aab-nav-pill' ) : [];
		}

		function updatePillStates( active ) {
			forEach( pills(), function ( pill ) {
				var on = pill.getAttribute( 'data-filter' ) === active;
				pill.classList.toggle( 'wg-aab-nav-pill--active', on );
				pill.setAttribute( 'aria-current', on ? 'true' : 'false' );
			} );
		}

		function scrollToGroup( key ) {
			if ( key === 'all' ) {
				scrollToElement( root );
				updatePillStates( 'all' );
				return;
			}
			var target = groupsWrap.querySelector( '[data-nav-group="' + key + '"]' );
			if ( target ) {
				scrollToElement( target );
			}
			updatePillStates( key );
		}

		function setMoreState( expanded ) {
			if ( ! pillsWrap ) {
				return;
			}
			var more = pillsWrap.querySelector( '.wg-aab-nav-more' );
			pillsWrap.classList.toggle( 'is-expanded', expanded );
			if ( more ) {
				more.setAttribute( 'aria-expanded', expanded ? 'true' : 'false' );
				more.textContent = expanded
					? ( more.getAttribute( 'data-less-label' ) || 'Show fewer' )
					: ( more.getAttribute( 'data-more-label' ) || more.textContent );
			}
		}

		if ( pillsWrap ) {
			// Only hide overflow pills once JS is running; without it they all wrap.
			pillsWrap.classList.add( 'wg-aab-pills--collapsible' );

			pillsWrap.addEventListener( 'click', function ( e ) {
				if ( closest( e.target, '.wg-aab-nav-more' ) ) {
					setMoreState( ! pillsWrap.classList.contains( 'is-expanded' ) );
					return;
				}
				var pill = closest( e.target, '.wg-aab-nav-pill' );
				if ( ! pill ) {
					return;
				}
				// Clear any active search first so the target section is visible.
				if ( searchInput && searchInput.value.trim() !== '' ) {
					searchInput.value = '';
					resetSearch();
				}
				scrollToGroup( pill.getAttribute( 'data-filter' ) );
			} );

			// Arrow keys move between the visible buttons.
			pillsWrap.addEventListener( 'keydown', function ( e ) {
				if ( [ 'ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp' ].indexOf( e.key ) === -1 ) {
					return;
				}
				var buttons = Array.prototype.filter.call( pillsWrap.querySelectorAll( 'button' ), function ( b ) {
					return b.offsetParent !== null;
				} );
				var index = buttons.indexOf( document.activeElement );
				if ( index === -1 ) {
					return;
				}
				e.preventDefault();
				var forward = ( e.key === 'ArrowRight' || e.key === 'ArrowDown' );
				buttons[ ( index + ( forward ? 1 : buttons.length - 1 ) ) % buttons.length ].focus();
			} );
		}

		// Active pill follows the scroll position.
		var trackTimer;
		function trackActiveSection() {
			clearTimeout( trackTimer );
			trackTimer = setTimeout( function () {
				if ( searchInput && searchInput.value.trim() !== '' ) {
					return;
				}
				var sections = groupsWrap.querySelectorAll( '[data-nav-group]' );
				var offset   = navOffset() + 40;
				var active   = 'all';
				for ( var i = 0; i < sections.length; i++ ) {
					var rect = sections[ i ].getBoundingClientRect();
					if ( rect.top <= offset && rect.bottom > offset ) {
						active = sections[ i ].getAttribute( 'data-nav-group' );
						break;
					}
				}
				updatePillStates( active );
			}, 100 );
		}
		window.addEventListener( 'scroll', trackActiveSection, { passive: true } );
		window.addEventListener( 'resize', function () {
			syncAdminBarOffset();
			trackActiveSection();
		} );

		// -------------------------------------------------------------------
		// Search (the only thing that hides/shows content)
		// -------------------------------------------------------------------
		function show( el ) {
			el.classList.remove( HIDDEN );
		}

		function hide( el ) {
			el.classList.add( HIDDEN );
		}

		function updateCount( shown ) {
			if ( ! countEl ) {
				return;
			}
			if ( shown === total ) {
				countEl.textContent = sprintf(
					total === 1 ? ( i18n.countOne || '%s question' ) : ( i18n.count || '%s questions' ),
					total
				);
			} else {
				countEl.textContent = sprintf( i18n.countFiltered || '%1$s of %2$s questions', shown, total );
			}
		}

		function restoreCollapsed() {
			forEach( groupsWrap.querySelectorAll( 'details.wg-aab-more[data-auto-opened]' ), function ( details ) {
				details.open = false;
				details.removeAttribute( 'data-auto-opened' );
			} );
		}

		function resetSearch() {
			forEach( groupsWrap.querySelectorAll( '.' + HIDDEN ), show );
			restoreCollapsed();
			if ( noResults ) {
				noResults.style.display = 'none';
			}
			updateCount( total );
		}

		function runSearch( query ) {
			var tokens = normalizeText( query ).split( ' ' ).filter( Boolean );
			if ( ! tokens.length ) {
				resetSearch();
				return;
			}

			// No section is "current" while searching.
			updatePillStates( '' );

			forEach( groupsWrap.querySelectorAll( '.' + HIDDEN ), show );

			var shown = 0;
			items.forEach( function ( item ) {
				var match = true;
				for ( var i = 0; i < tokens.length; i++ ) {
					if ( item.search.indexOf( tokens[ i ] ) === -1 ) {
						match = false;
						break;
					}
				}
				item.el.classList.toggle( HIDDEN, ! match );
				if ( match ) {
					shown++;
				}
			} );

			// Curated layout: open collapsed groups with matches, hide the rest.
			forEach( groupsWrap.querySelectorAll( 'details.wg-aab-more' ), function ( details ) {
				var hasVisible = details.querySelector( '.wg-aab-item:not(.' + HIDDEN + ')' );
				if ( hasVisible ) {
					if ( ! details.open ) {
						details.open = true;
						details.setAttribute( 'data-auto-opened', '1' );
					}
				} else {
					hide( details );
				}
			} );

			// Hide sections with nothing visible inside.
			forEach( groupsWrap.querySelectorAll( '[data-nav-group]' ), function ( section ) {
				section.classList.toggle( HIDDEN, ! section.querySelector( '.wg-aab-item:not(.' + HIDDEN + ')' ) );
			} );

			if ( noResults ) {
				noResults.style.display = shown ? 'none' : 'block';
			}
			updateCount( shown );
		}

		var searchTimer;
		if ( searchInput ) {
			searchInput.addEventListener( 'input', function () {
				clearTimeout( searchTimer );
				searchTimer = setTimeout( function () {
					runSearch( searchInput.value.trim() );
				}, 200 );
			} );
			searchInput.addEventListener( 'keydown', function ( e ) {
				if ( e.key === 'Escape' ) {
					searchInput.value = '';
					resetSearch();
					searchInput.blur();
				}
			} );
		}

		// -------------------------------------------------------------------
		// Answer Library: "By category" / "A–Z" regrouping
		// -------------------------------------------------------------------
		var currentView = 'category';
		var styles = {
			section:     root.getAttribute( 'data-section-style' ) || '',
			sectionAlt:  root.getAttribute( 'data-section-style-alt' ) || '',
			container:   root.getAttribute( 'data-container-style' ) || '',
			heading:     root.getAttribute( 'data-heading-style' ) || '',
			headingLink: root.getAttribute( 'data-heading-link-style' ) || ''
		};

		function groupOrder( key ) {
			var meta = groupMeta[ key ];
			return meta && typeof meta.order === 'number' ? meta.order : 9999;
		}

		function buildGroups( view ) {
			var groups = [];
			var byKey  = {};
			var sorted = items.slice();

			if ( view === 'az' ) {
				sorted.sort( function ( a, b ) {
					if ( a.sort < b.sort ) { return -1; }
					if ( a.sort > b.sort ) { return 1; }
					return a.order - b.order;
				} );
				sorted.forEach( function ( item ) {
					var key = item.letter === '#' ? 'letter-other' : 'letter-' + item.letter;
					if ( ! byKey[ key ] ) {
						byKey[ key ] = {
							key:   key,
							label: item.letter === '#' ? ( i18n.other || '#' ) : item.letter,
							url:   '',
							id:    baseId + '-' + key,
							items: []
						};
						groups.push( byKey[ key ] );
					}
					byKey[ key ].items.push( item );
				} );
				// Letters A–Z, then the digits/symbols bucket last.
				groups.sort( function ( a, b ) {
					if ( a.key === 'letter-other' ) { return 1; }
					if ( b.key === 'letter-other' ) { return -1; }
					return a.label < b.label ? -1 : 1;
				} );
			} else {
				sorted.sort( function ( a, b ) {
					var oa = groupOrder( a.group );
					var ob = groupOrder( b.group );
					return oa !== ob ? oa - ob : a.order - b.order;
				} );
				sorted.forEach( function ( item ) {
					var key  = item.group;
					var meta = groupMeta[ key ] || {};
					if ( ! byKey[ key ] ) {
						byKey[ key ] = {
							key:   key,
							label: meta.label || key,
							url:   meta.url || '',
							id:    meta.id || ( baseId + '-' + key ),
							items: []
						};
						groups.push( byKey[ key ] );
					}
					byKey[ key ].items.push( item );
				} );
			}
			return groups;
		}

		function buildSection( group, index, showHeading ) {
			var alt     = ( index % 2 === 1 );
			var section = document.createElement( 'div' );
			section.className = 'wg-aab-category-section' + ( alt ? ' wg-aab-category-section--alternate' : '' );
			var sectionStyle = alt ? styles.sectionAlt : styles.section;
			if ( sectionStyle ) {
				section.setAttribute( 'style', sectionStyle );
			}
			section.setAttribute( 'data-nav-group', group.key );

			var container = document.createElement( 'div' );
			container.className = 'wg-aab-content-container';
			if ( styles.container ) {
				container.setAttribute( 'style', styles.container );
			}

			if ( showHeading ) {
				var heading = document.createElement( 'h2' );
				heading.className = 'wg-aab-category-heading';
				heading.id = group.id;
				if ( styles.heading ) {
					heading.setAttribute( 'style', styles.heading );
				}
				if ( group.url ) {
					var link = document.createElement( 'a' );
					link.href = group.url;
					link.textContent = group.label;
					if ( styles.headingLink ) {
						link.setAttribute( 'style', styles.headingLink );
					}
					heading.appendChild( link );
				} else {
					heading.textContent = group.label;
				}
				container.appendChild( heading );
			}

			var list = document.createElement( 'div' );
			list.className = 'wg-aab-category';
			group.items.forEach( function ( item ) {
				list.appendChild( item.el ); // moves the existing node
			} );
			container.appendChild( list );
			section.appendChild( container );
			return section;
		}

		function rebuildPills( groups, collapsible ) {
			if ( ! pillsWrap ) {
				return;
			}
			pillsWrap.classList.toggle( HIDDEN, groups.length <= 1 );
			var limit = parseInt( pillsWrap.getAttribute( 'data-pill-limit' ) || '6', 10 );
			while ( pillsWrap.firstChild ) {
				pillsWrap.removeChild( pillsWrap.firstChild );
			}

			var all = document.createElement( 'button' );
			all.type = 'button';
			all.className = 'wg-aab-nav-pill wg-aab-nav-pill--active';
			all.setAttribute( 'data-filter', 'all' );
			all.setAttribute( 'aria-current', 'true' );
			all.textContent = i18n.all || 'All';
			pillsWrap.appendChild( all );

			var hidden = 0;
			groups.forEach( function ( g, i ) {
				var overflow = collapsible && i >= limit;
				if ( overflow ) {
					hidden++;
				}
				var pill = document.createElement( 'button' );
				pill.type = 'button';
				pill.className = 'wg-aab-nav-pill' + ( overflow ? ' wg-aab-nav-pill--overflow' : '' );
				pill.setAttribute( 'data-filter', g.key );
				pill.setAttribute( 'aria-current', 'false' );
				pill.textContent = g.label;
				pillsWrap.appendChild( pill );
			} );

			if ( hidden > 0 ) {
				var more      = document.createElement( 'button' );
				var moreLabel = sprintf( i18n.more || '+%s more', hidden );
				more.type = 'button';
				more.className = 'wg-aab-nav-more';
				more.setAttribute( 'aria-expanded', 'false' );
				more.setAttribute( 'data-more-label', moreLabel );
				more.setAttribute( 'data-less-label', i18n.less || 'Show fewer' );
				more.textContent = moreLabel;
				pillsWrap.appendChild( more );
			}
			pillsWrap.classList.remove( 'is-expanded' );
		}

		function regroup( view ) {
			if ( view === currentView ) {
				return;
			}
			var groups = buildGroups( view );
			var multi  = groups.length > 1;
			var frag   = document.createDocumentFragment();

			groups.forEach( function ( g, i ) {
				var showHeading = ( view === 'az' ) || multi || g.key !== 'uncategorized';
				frag.appendChild( buildSection( g, i, showHeading ) );
			} );

			// Old sections are empty shells now; replace them wholesale.
			while ( groupsWrap.firstChild ) {
				groupsWrap.removeChild( groupsWrap.firstChild );
			}
			groupsWrap.appendChild( frag );
			currentView = view;

			// Letter pills are short; show them all. Category pills keep the "+N more" rule.
			rebuildPills( groups, view === 'category' );

			forEach( viewBtns, function ( btn ) {
				var on = btn.getAttribute( 'data-view' ) === view;
				btn.classList.toggle( 'wg-aab-nav-pill--active', on );
				btn.setAttribute( 'aria-pressed', on ? 'true' : 'false' );
			} );

			if ( searchInput && searchInput.value.trim() !== '' ) {
				runSearch( searchInput.value.trim() );
			} else {
				updatePillStates( 'all' );
			}
			writeView( view );
		}

		forEach( viewBtns, function ( btn ) {
			btn.addEventListener( 'click', function () {
				regroup( btn.getAttribute( 'data-view' ) === 'az' ? 'az' : 'category' );
			} );
		} );

		// -------------------------------------------------------------------
		// URL state: ?aab_view=az (library) and the legacy #cat=key hash
		// -------------------------------------------------------------------
		function writeView( view ) {
			if ( ! window.history || ! window.history.replaceState ) {
				return;
			}
			var url;
			try {
				url = new URL( window.location.href );
			} catch ( e ) {
				return;
			}
			if ( view === 'az' ) {
				url.searchParams.set( 'aab_view', 'az' );
			} else {
				url.searchParams.delete( 'aab_view' );
			}
			var next = url.toString();
			if ( next !== window.location.href ) {
				window.history.replaceState( null, '', next );
			}
		}

		if ( layout === 'library' ) {
			var wantsAz = false;
			try {
				wantsAz = new URLSearchParams( window.location.search ).get( 'aab_view' ) === 'az';
			} catch ( e ) {
				wantsAz = false;
			}
			if ( wantsAz ) {
				regroup( 'az' );
			}
		}

		var hashMatch = window.location.hash.match( /^#cat=(.+)$/ );
		if ( hashMatch ) {
			scrollToGroup( decodeURIComponent( hashMatch[1] ) );
		}

		updateCount( total );
	}

	forEach( document.querySelectorAll( '.wg-aab-listing' ), initListing );
})();
